import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash, Plus, X, FloppyDisk, MagnifyingGlass, ToggleLeft, ToggleRight } from "@phosphor-icons/react";

/**
 * Gestión de reglas de auto-importación de siniestros desde correo IMAP.
 *
 * Cada regla define:
 *  - carpeta IMAP donde buscar
 *  - remitentes que disparan la regla (from_contains)
 *  - patrones de asunto que deben coincidir (subject_contains)
 *  - aseguradora esperada (etiqueta de la cuenta)
 */
export default function IntakeRulesManager({ msConnected }) {
  const [rules, setRules] = useState([]);
  const [folders, setFolders] = useState([]);
  const [gabineteMap, setGabineteMap] = useState({});
  const [editing, setEditing] = useState(null); // null | {} | rule
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [r, gr] = await Promise.all([
        api.get("/settings/imap/intake/rules"),
        api.get("/gabinetes").catch(() => ({ data: [] })),
      ]);
      setRules(r.data || []);
      const map = {};
      (gr.data || []).forEach((g) => { map[g.id] = g; });
      setGabineteMap(map);
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      const r = await api.get("/settings/imap/folders");
      if (r.data?.ok) setFolders(r.data.folders || []);
    } catch (e) {
      // silencioso: si no hay conexión IMAP no hay carpetas
    }
  };

  useEffect(() => {
    reload();
    loadFolders();
  }, []);

  const toggle = async (rule) => {
    try {
      await api.put(`/settings/imap/intake/rules/${rule.id}`, { active: !rule.active });
      toast.success(`Regla ${rule.active ? "desactivada" : "activada"}`);
      reload();
    } catch (e) {
      toast.error("No se pudo cambiar el estado");
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`¿Eliminar la regla «${rule.name}»? No se borrarán los expedientes ya creados.`)) return;
    try {
      await api.delete(`/settings/imap/intake/rules/${rule.id}`);
      toast.success("Regla eliminada");
      reload();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const preview = async (rule) => {
    setPreviewing(rule.id);
    try {
      // Activamos sólo esta regla temporalmente NO — mejor llamar al endpoint y filtrar luego.
      const r = await api.post(`/settings/imap/intake?only_unseen=false&since_days=14&dry_run=true`);
      const items = (r.data?.items || []).filter((x) => x.rule === rule.id);
      toast.message(`Vista previa · ${items.length} correo(s) coincidirían (últimos 14 días) · ${rule.folder}`, {
        description: items.slice(0, 3).map((i) => `${i.subject} (${i.pdfs?.length || 0} PDF)`).join(" · ") || "Sin coincidencias",
      });
    } catch (e) {
      toast.error("No se pudo hacer la vista previa");
    } finally {
      setPreviewing(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="intake-rules-manager">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-foreground">Reglas de auto-importación</h4>
          <p className="text-[11px] text-muted mt-0.5">
            Cada regla escanea una carpeta IMAP buscando correos de un remitente con un patrón de asunto.
            Al detectar coincidencias, crea expedientes desde los PDFs adjuntos.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({})}
          data-testid="add-rule-btn"
          disabled={!msConnected && folders.length === 0}
          className="btn-brand text-xs px-3 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Plus className="w-3.5 h-3.5" weight="bold" />
          Nueva regla
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-muted py-4 text-center">Cargando reglas…</div>
      ) : rules.length === 0 ? (
        <div className="text-xs text-muted py-6 text-center border border-dashed rounded-md bg-slate-50">
          Aún no hay reglas configuradas. Crea una para empezar.
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map((r) => (
            <div
              key={r.id}
              data-testid={`rule-row-${r.id}`}
              className={`border rounded-md p-3 flex items-start gap-3 ${r.active ? "bg-white border-border" : "bg-slate-50 border-slate-200 opacity-70"}`}
            >
              <button
                type="button"
                onClick={() => toggle(r)}
                title={r.active ? "Activa — pulsa para desactivar" : "Inactiva — pulsa para activar"}
                className="shrink-0 mt-0.5"
                data-testid={`toggle-rule-${r.id}`}
              >
                {r.active ? (
                  <ToggleRight className="w-7 h-7 text-emerald-600" weight="fill" />
                ) : (
                  <ToggleLeft className="w-7 h-7 text-slate-400" weight="fill" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-sm font-semibold text-foreground">{r.name}</span>
                  {r.expected_aseguradora && (
                    <span className="text-[10px] uppercase tracking-wide bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded font-bold">
                      {r.expected_aseguradora}
                    </span>
                  )}
                  {r.gabinete_id && gabineteMap[r.gabinete_id] && (
                    <span className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded font-bold" title="Gabinete intermediario">
                      via {gabineteMap[r.gabinete_id].short_label || gabineteMap[r.gabinete_id].name}
                    </span>
                  )}
                  {r.mode_one_per_email && (
                    <span className="text-[10px] uppercase tracking-wide bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded font-bold" title="1 email = 1 expediente (agrupa adjuntos)">
                      1 email = 1 exp.
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-1 mt-1 text-[11px] text-muted">
                  <div>📁 <strong className="text-foreground">{r.folder || "INBOX"}</strong></div>
                  <div>✉ {(r.from_contains || []).join(", ") || <em>(cualquier remitente)</em>}</div>
                  <div>🔖 {(r.subject_contains || []).join(", ") || <em>(cualquier asunto)</em>}</div>
                  {(r.subject_excludes || []).length > 0 && (
                    <div className="md:col-span-3 text-rose-600">🚫 excluye: {r.subject_excludes.join(", ")}</div>
                  )}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => preview(r)}
                  disabled={previewing === r.id}
                  title="Vista previa: ¿qué correos coincidirían en los últimos 14 días?"
                  className="text-[11px] border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded px-2 py-1 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid={`preview-rule-${r.id}`}
                >
                  <MagnifyingGlass className="w-3 h-3" weight="bold" />
                  {previewing === r.id ? "..." : "Probar"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(r)}
                  className="text-[11px] border border-border hover:border-foreground rounded px-2 py-1 inline-flex items-center gap-1 bg-white"
                  data-testid={`edit-rule-${r.id}`}
                >
                  <Pencil className="w-3 h-3" weight="bold" />
                  Editar
                </button>
                <button
                  type="button"
                  onClick={() => remove(r)}
                  className="text-[11px] border border-rose-300 hover:bg-rose-50 text-rose-700 rounded px-2 py-1 inline-flex items-center gap-1 bg-white"
                  data-testid={`delete-rule-${r.id}`}
                >
                  <Trash className="w-3 h-3" weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <RuleEditor
          rule={editing}
          folders={folders}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function RuleEditor({ rule, folders, onClose, onSaved }) {
  const isNew = !rule.id;
  const [gabinetes, setGabinetes] = useState([]);
  const [form, setForm] = useState({
    name: rule.name || "",
    folder: rule.folder || "INBOX",
    from_contains: (rule.from_contains || []).join(", "),
    subject_contains: (rule.subject_contains || []).join(", "),
    subject_excludes: (rule.subject_excludes || []).join(", "),
    expected_aseguradora: rule.expected_aseguradora || "",
    gabinete_id: rule.gabinete_id || "",
    mode_one_per_email: rule.mode_one_per_email === true,
    active: rule.active !== false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/gabinetes")
      .then((r) => setGabinetes(r.data || []))
      .catch(() => setGabinetes([]));
  }, []);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        folder: form.folder || "INBOX",
        from_contains: form.from_contains.split(",").map((s) => s.trim()).filter(Boolean),
        subject_contains: form.subject_contains.split(",").map((s) => s.trim()).filter(Boolean),
        subject_excludes: form.subject_excludes.split(",").map((s) => s.trim()).filter(Boolean),
        expected_aseguradora: form.expected_aseguradora.trim(),
        gabinete_id: form.gabinete_id || null,
        mode_one_per_email: form.mode_one_per_email,
        active: form.active,
      };
      if (isNew) {
        await api.post("/settings/imap/intake/rules", payload);
        toast.success("Regla creada");
      } else {
        await api.put(`/settings/imap/intake/rules/${rule.id}`, payload);
        toast.success("Regla guardada");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la regla");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="rule-editor-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl border border-border">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-base font-bold">{isNew ? "Nueva regla de importación" : "Editar regla"}</h3>
          <button type="button" onClick={onClose} className="text-muted hover:text-foreground" data-testid="rule-editor-close">
            <X className="w-5 h-5" weight="bold" />
          </button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <Field label="Nombre" hint="Ej: «Mapfre · Encargos Línea Hogar»">
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Mapfre · Encargos"
              data-testid="rule-name"
              autoFocus
            />
          </Field>
          <Field label="Carpeta IMAP donde buscar" hint="Selecciona una carpeta existente del buzón">
            {folders.length > 0 ? (
              <select
                value={form.folder}
                onChange={(e) => setForm((f) => ({ ...f, folder: e.target.value }))}
                data-testid="rule-folder"
              >
                {folders.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            ) : (
              <input
                value={form.folder}
                onChange={(e) => setForm((f) => ({ ...f, folder: e.target.value }))}
                placeholder="INBOX"
                data-testid="rule-folder"
              />
            )}
          </Field>
          <Field label="Remitente debe contener" hint="Separa varios con coma. Ej: «mapfre.com, peritaciones@mapfre.es»">
            <input
              value={form.from_contains}
              onChange={(e) => setForm((f) => ({ ...f, from_contains: e.target.value }))}
              placeholder="profesionales@aseguradora.es"
              data-testid="rule-from"
            />
          </Field>
          <Field label="Asunto debe contener" hint="Separa varios con coma. Si pones varios, basta con que coincida UNO de ellos.">
            <input
              value={form.subject_contains}
              onChange={(e) => setForm((f) => ({ ...f, subject_contains: e.target.value }))}
              placeholder="asignación, encargo, nuevo siniestro"
              data-testid="rule-subject"
            />
          </Field>
          <Field label="Asunto NO debe contener (exclusiones)" hint="Útil para descartar reenvíos. Ej: «fw:, re:, documentación, recordatorio»">
            <input
              value={form.subject_excludes}
              onChange={(e) => setForm((f) => ({ ...f, subject_excludes: e.target.value }))}
              placeholder="fw:, re:, documentación"
              data-testid="rule-subject-excludes"
            />
          </Field>
          <Field label="Aseguradora asociada" hint="Nombre de la aseguradora (debe coincidir con la dada de alta en Aseguradoras)">
            <input
              value={form.expected_aseguradora}
              onChange={(e) => setForm((f) => ({ ...f, expected_aseguradora: e.target.value }))}
              placeholder="Mapfre"
              data-testid="rule-aseguradora"
            />
          </Field>
          <Field label="Gabinete intermediario (opcional)" hint="Si el expediente llega vía un gabinete tramitador (ej. Calzado, Gefoex), selecciónalo. Se asignará automáticamente al expediente creado.">
            <select
              value={form.gabinete_id}
              onChange={(e) => setForm((f) => ({ ...f, gabinete_id: e.target.value }))}
              data-testid="rule-gabinete"
            >
              <option value="">— Ninguno (encargo directo) —</option>
              {gabinetes.map((g) => (
                <option key={g.id} value={g.id}>{g.name}{g.short_label ? ` (${g.short_label})` : ""}</option>
              ))}
            </select>
          </Field>
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={form.mode_one_per_email}
              onChange={(e) => setForm((f) => ({ ...f, mode_one_per_email: e.target.checked }))}
              data-testid="rule-one-per-email"
            />
            <span>
              <strong>1 email = 1 expediente</strong> (agrupa todos los adjuntos en un único expediente).
              <br />
              <span className="text-muted text-[10px]">
                Recomendado para Santander (envía PDF + XLS del mismo siniestro). Dejar desmarcado para Ocaso/Mapfre (cada PDF es un siniestro distinto).
              </span>
            </span>
          </label>
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              data-testid="rule-active"
            />
            Regla activa (se ejecuta cada 15 min y al pulsar Auto-importar)
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="text-xs border border-border bg-white hover:border-foreground rounded-md px-3 py-2"
            data-testid="rule-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="rule-save"
            className="btn-brand text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <FloppyDisk className="w-3.5 h-3.5" weight="bold" />
            {saving ? "Guardando..." : (isNew ? "Crear regla" : "Guardar cambios")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-foreground mb-1">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-muted mt-1">{hint}</div>}
    </div>
  );
}
